import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiCreditCard, FiSave, FiEdit2, FiFileText, FiUpload, FiCheckCircle, FiAlertCircle, FiFile, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    contactNumber: '',
    email: '',
    identityCard: '',
    username: '',
  });
  const [restaurantData, setRestaurantData] = useState({
    restaurantName: '',
    city: '',
    address: '',
    latitude: '',
    longitude: '',
    fssaiNumber: '',
    gstNumber: ''
  });
  const [vendorData, setVendorData] = useState(null);
  const [documents, setDocuments] = useState({
    fssaiCertificate: '',
    gstCertificate: '',
    tradeLicense: '',
    restaurantLogo: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/vendor/profile');
      if (response.data.success) {
        const vendor = response.data.data;
        setVendorData(vendor);
        const data = {
          name: vendor.personalDetails?.name || '',
          contactNumber: vendor.personalDetails?.contactNumber || '',
          email: vendor.personalDetails?.email || '',
          identityCard: vendor.personalDetails?.identityCard || '',
          username: vendor.ownerCredentials?.username || '',
        };
        const restData = {
          restaurantName: vendor.restaurantDetails?.restaurantName || '',
          city: vendor.restaurantDetails?.city || '',
          address: vendor.restaurantDetails?.address || '',
          latitude: vendor.restaurantDetails?.location?.coordinates?.[1] || '',
          longitude: vendor.restaurantDetails?.location?.coordinates?.[0] || '',
          fssaiNumber: vendor.restaurantDetails?.fssaiNumber || '',
          gstNumber: vendor.restaurantDetails?.gstNumber || ''
        };
        setProfileData(data);
        setRestaurantData(restData);
        setDocuments({
          fssaiCertificate: vendor.documents?.fssaiCertificate || '',
          gstCertificate: vendor.documents?.gstCertificate || '',
          tradeLicense: vendor.documents?.tradeLicense || '',
          restaurantLogo: vendor.documents?.restaurantLogo || ''
        });
      }
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangePassword = async () => {
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await api.post('/vendor/change-password', {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        toast.success('Password updated successfully');
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveRestaurantDetails = async () => {
    if (!restaurantData.fssaiNumber && !restaurantData.gstNumber) {
      toast.error('Please enter at least one of FSSAI or GST number');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/vendor/update-restaurant-details', {
        fssaiNumber: restaurantData.fssaiNumber,
        gstNumber: restaurantData.gstNumber
      });

      if (response.data.success) {
        toast.success('Restaurant details updated successfully');
        setIsEditing(false);
        fetchProfile();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update restaurant details');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (vendorData?.documentsApproved) {
      toast.error('Documents are already approved by admin and cannot be changed.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.fileUrl;
        const updateData = {
          documents: {
            ...documents,
            [docType]: fileUrl
          }
        };

        const updateResponse = await api.put('/vendor/update-documents', updateData);
        if (updateResponse.data.success) {
          setDocuments(prev => ({ ...prev, [docType]: fileUrl }));
          toast.success('Document uploaded successfully');
          fetchProfile();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const openDocument = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isPDF = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf');
  };

  const isWordDoc = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.doc') || url.toLowerCase().includes('.docx');
  };

  const isExcelDoc = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.xls') || url.toLowerCase().includes('.xlsx');
  };

  const isTextDoc = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.txt');
  };

  const isDocument = (url) => {
    return isPDF(url) || isWordDoc(url) || isExcelDoc(url) || isTextDoc(url);
  };

  const getDocumentIcon = (url) => {
    if (isPDF(url)) return { icon: '📄', label: 'PDF Document', color: '#dc3545' };
    if (isWordDoc(url)) return { icon: '📝', label: 'Word Document', color: '#2b579a' };
    if (isExcelDoc(url)) return { icon: '📊', label: 'Excel Document', color: '#217346' };
    if (isTextDoc(url)) return { icon: '📃', label: 'Text Document', color: '#6c757d' };
    return { icon: '📎', label: 'Document', color: '#6c757d' };
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4 md:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 flex items-center gap-4">
        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white">
          <FiUser className="text-xl" />
        </div>

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            My Profile
          </h1>
          <p className="text-gray-600">Manage your personal and restaurant information</p>
        </div>
      </div>


      {/* Personal Details Section */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FiUser className="text-blue-600" />
          Personal Details
        </h3>

        <div className="space-y-6">
          {/* Name */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiUser size={18} />
              Owner Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={profileData.name}
              disabled={true}
              placeholder="Enter your full name"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Contact Number */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiPhone size={18} />
              Contact Number *
            </label>
            <input
              type="text"
              name="contactNumber"
              value={profileData.contactNumber}
              disabled={true}
              placeholder="10-digit mobile number"
              maxLength="10"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiMail size={18} />
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={profileData.email}
              disabled={true}
              placeholder="Enter your email address"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Username */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiMail size={18} />
              Username *
            </label>
            <input
              type="text"
              name="username"
              value={profileData.username}
              disabled={true}
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Identity Card */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiCreditCard size={18} />
              Identity Card Number (Aadhaar/PAN)
            </label>
            <input
              type="text"
              name="identityCard"
              value={profileData.identityCard}
              disabled={true}
              placeholder="Enter identity card number"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
          <strong>Note:</strong> Personal and restaurant details cannot be modified from this page. Contact support if you need to update this information.
        </div>
      </div>

      {/* Restaurant Details Section */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FiFileText className="text-blue-600" />
          Restaurant Details
        </h3>

        <div className="space-y-6">
          {/* Restaurant Name */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiFileText size={18} />
              Restaurant Name *
            </label>
            <input
              type="text"
              name="restaurantName"
              value={restaurantData.restaurantName}
              disabled={true}
              placeholder="Enter restaurant name"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* City */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiFileText size={18} />
              City *
            </label>
            <input
              type="text"
              name="city"
              value={restaurantData.city}
              disabled={true}
              placeholder="Enter city"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Address */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiFileText size={18} />
              Address *
            </label>
            <textarea
              name="address"
              value={restaurantData.address}
              disabled={true}
              placeholder="Enter full address"
              rows="3"
              className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed resize-vertical"
            />
          </div>

          {/* Location Coordinates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
                <FiFileText size={18} />
                Latitude
              </label>
              <input
                type="text"
                name="latitude"
                value={restaurantData.latitude}
                disabled={true}
                placeholder="e.g., 28.7041"
                className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
                <FiFileText size={18} />
                Longitude
              </label>
              <input
                type="text"
                name="longitude"
                value={restaurantData.longitude}
                disabled={true}
                placeholder="e.g., 77.1025"
                className="w-full px-4 py-3 text-gray-900 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
              />
            </div>
          </div>

          {/* FSSAI Number */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiFileText size={18} />
              FSSAI Number
            </label>
            <input
              type="text"
              name="fssaiNumber"
              value={restaurantData.fssaiNumber}
              onChange={(e) => setRestaurantData({ ...restaurantData, fssaiNumber: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter FSSAI number"
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg ${
                isEditing 
                  ? 'bg-white focus:border-blue-500 focus:outline-none' 
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
            />
          </div>

          {/* GST Number */}
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiFileText size={18} />
              GST Number
            </label>
            <input
              type="text"
              name="gstNumber"
              value={restaurantData.gstNumber}
              onChange={(e) => setRestaurantData({ ...restaurantData, gstNumber: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter GST number"
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg ${
                isEditing 
                  ? 'bg-white focus:border-blue-500 focus:outline-none' 
                  : 'bg-gray-100 cursor-not-allowed'
              }`}
            />
          </div>
        </div>

        {/* Edit/Save Buttons */}
        <div className="mt-8 flex justify-end">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-2"
            >
              <FiEdit2 size={16} />
              Edit FSSAI & GST
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  fetchProfile();
                }}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors duration-200"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRestaurantDetails}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-2"
                disabled={saving}
              >
                <FiSave size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FiLock className="text-blue-600" size={24} />
          Change Password
        </h3>

        <div className="space-y-6">
          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiLock size={18} />
              Current Password *
            </label>
            <div className="relative">
              <input
                type={showOldPassword ? 'text' : 'password'}
                name="oldPassword"
                value={passwordData.oldPassword}
                onChange={handlePasswordChange}
                placeholder="Enter current password"
                className="w-full px-4 py-3 pr-12 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showOldPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiLock size={18} />
              New Password *
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password (min 6 characters)"
                className="w-full px-4 py-3 pr-12 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2 font-medium text-gray-700">
              <FiLock size={18} />
              Confirm New Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Re-enter new password"
                className="w-full px-4 py-3 pr-12 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleChangePassword}
            disabled={changingPassword}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FiLock /> {changingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FiFile className="text-blue-600" />
            Restaurant Documents
          </h3>
        </div>

        {vendorData?.documentsApproved && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg text-sm text-red-700">
            <strong>Important:</strong> Your documents have been approved by admin. You cannot upload new documents. Please contact support if you need to update them.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* FSSAI Certificate */}
          <div className="text-center">
            <h4 className="mb-3 text-sm font-medium text-gray-700">FSSAI Certificate</h4>
            {documents.fssaiCertificate ? (
              <div 
                onClick={() => openDocument(documents.fssaiCertificate)}
                className="w-full h-36 rounded-lg border-2 border-gray-300 cursor-pointer flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
                style={{
                  backgroundImage: isDocument(documents.fssaiCertificate) ? 'none' : `url(${documents.fssaiCertificate})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: isDocument(documents.fssaiCertificate) ? '#f8f9fa' : 'transparent'
                }}
              >
                {isDocument(documents.fssaiCertificate) && (
                  <div className="text-center" style={{ color: getDocumentIcon(documents.fssaiCertificate).color }}>
                    <div className="text-4xl">{getDocumentIcon(documents.fssaiCertificate).icon}</div>
                    <div className="text-xs mt-2">{getDocumentIcon(documents.fssaiCertificate).label}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-36 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <FiFileText size={40} className="text-gray-400" />
              </div>
            )}
            {!vendorData?.documentsApproved && (
              <label className="mt-3 inline-block cursor-pointer">
                <input
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => handleDocumentUpload(e, 'fssaiCertificate')}
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors duration-200 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <FiUpload size={14} /> {documents.fssaiCertificate ? 'Change' : 'Upload'}
                </span>
              </label>
            )}
          </div>

          {/* GST Certificate */}
          <div className="text-center">
            <h4 className="mb-3 text-sm font-medium text-gray-700">GST Certificate</h4>
            {documents.gstCertificate ? (
              <div 
                onClick={() => openDocument(documents.gstCertificate)}
                className="w-full h-36 rounded-lg border-2 border-gray-300 cursor-pointer flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
                style={{
                  backgroundImage: isDocument(documents.gstCertificate) ? 'none' : `url(${documents.gstCertificate})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: isDocument(documents.gstCertificate) ? '#f8f9fa' : 'transparent'
                }}
              >
                {isDocument(documents.gstCertificate) && (
                  <div className="text-center" style={{ color: getDocumentIcon(documents.gstCertificate).color }}>
                    <div className="text-4xl">{getDocumentIcon(documents.gstCertificate).icon}</div>
                    <div className="text-xs mt-2">{getDocumentIcon(documents.gstCertificate).label}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-36 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <FiFileText size={40} className="text-gray-400" />
              </div>
            )}
            {!vendorData?.documentsApproved && (
              <label className="mt-3 inline-block cursor-pointer">
                <input
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => handleDocumentUpload(e, 'gstCertificate')}
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors duration-200 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <FiUpload size={14} /> {documents.gstCertificate ? 'Change' : 'Upload'}
                </span>
              </label>
            )}
          </div>

          {/* Trade License */}
          <div className="text-center">
            <h4 className="mb-3 text-sm font-medium text-gray-700">Trade License</h4>
            {documents.tradeLicense ? (
              <div 
                onClick={() => openDocument(documents.tradeLicense)}
                className="w-full h-36 rounded-lg border-2 border-gray-300 cursor-pointer flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
                style={{
                  backgroundImage: isDocument(documents.tradeLicense) ? 'none' : `url(${documents.tradeLicense})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: isDocument(documents.tradeLicense) ? '#f8f9fa' : 'transparent'
                }}
              >
                {isDocument(documents.tradeLicense) && (
                  <div className="text-center" style={{ color: getDocumentIcon(documents.tradeLicense).color }}>
                    <div className="text-4xl">{getDocumentIcon(documents.tradeLicense).icon}</div>
                    <div className="text-xs mt-2">{getDocumentIcon(documents.tradeLicense).label}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-36 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <FiFileText size={40} className="text-gray-400" />
              </div>
            )}
            {!vendorData?.documentsApproved && (
              <label className="mt-3 inline-block cursor-pointer">
                <input
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => handleDocumentUpload(e, 'tradeLicense')}
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors duration-200 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <FiUpload size={14} /> {documents.tradeLicense ? 'Change' : 'Upload'}
                </span>
              </label>
            )}
          </div>

          {/* Restaurant Logo */}
          <div className="text-center">
            <h4 className="mb-3 text-sm font-medium text-gray-700">Restaurant Logo</h4>
            {documents.restaurantLogo ? (
              <div 
                onClick={() => openDocument(documents.restaurantLogo)}
                className="w-full h-36 rounded-lg border-2 border-gray-300 cursor-pointer flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
                style={{
                  backgroundImage: isDocument(documents.restaurantLogo) ? 'none' : `url(${documents.restaurantLogo})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: isDocument(documents.restaurantLogo) ? '#f8f9fa' : 'transparent'
                }}
              >
                {isDocument(documents.restaurantLogo) && (
                  <div className="text-center" style={{ color: getDocumentIcon(documents.restaurantLogo).color }}>
                    <div className="text-4xl">{getDocumentIcon(documents.restaurantLogo).icon}</div>
                    <div className="text-xs mt-2">{getDocumentIcon(documents.restaurantLogo).label}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-36 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <FiFileText size={40} className="text-gray-400" />
              </div>
            )}
            {!vendorData?.documentsApproved && (
              <label className="mt-3 inline-block cursor-pointer">
                <input
                  type="file"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => handleDocumentUpload(e, 'restaurantLogo')}
                  className="hidden"
                  disabled={uploading}
                />
                <span className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors duration-200 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <FiUpload size={14} /> {documents.restaurantLogo ? 'Change' : 'Upload'}
                </span>
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorProfile;
